import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';

import useLocale from '@/hooks/useLocale';
import { useIsMobile } from '@/hooks/useIsMobile';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import { Button } from 'gatorcomponents';

import * as Styles from './UpgradeListing.styles';
import PeriodSelector from './components/PeriodSelector/PeriodSelector';
import PlansList from './components/PlansList/PlansList';
import PlanFeatures from './components/PlanFeatures/PlanFeatures';
import PerformanceBanner from './components/PerformanceBanner/PerformanceBanner';

const parseNumericValue = (str) => {
  if (!str) return null;
  const num = parseFloat(str.replace(/[^\d.,]/g, '').replace(',', '.'));
  return Number.isNaN(num) ? null : num;
};

const computeResourceDelta = (currentVal, planVal, testId) => {
  if (!currentVal || !planVal) return null;
  const currentNum = parseNumericValue(currentVal);
  const planNum = parseNumericValue(planVal);
  if (currentNum == null || planNum == null) return null;
  const diff = planNum - currentNum;
  if (diff <= 0) return null;
  if (testId === 'processor') return `+${diff}`;
  const unit = planVal.replace(/[\d.,]/g, '').trim().split(' ').slice(-1)[0];
  return `+${diff} ${unit}`;
};

const buildResourcesList = (resources, currentResources) => {
  const KEYS = ['processor', 'memory', 'disk'];
  return KEYS
    .map(key => ({
      value: resources[key],
      testId: key,
      delta: computeResourceDelta(currentResources?.[key], resources[key], key),
    }))
    .filter(r => r.value != null);
};

const processPlanData = (plan, selectedCycle, currentResources) => {
  const resources = plan.resources || {};
  const price = plan.prices?.find(p => p.billingCycle === selectedCycle);
  return {
    resourcesList: buildResourcesList(resources, currentResources),
    billingCycle: price?.billingCycle,
    fullPrice: price?.fullPrice,
    monthlyPrice: price?.monthlyPrice,
    discountPercentage: price?.discountPercentage,
    coupon: price?.coupon,
  };
};

const UpgradeListing = ({ onSelectPlan }) => {
  const dispatch = useDispatch();
  const { id: hostingId } = useParams();
  const isMobile = useIsMobile();

  const [selectedCycle, setSelectedCycle] = useState(null);
  const [loadingPlanId, setLoadingPlanId] = useState(null);

  const upgradeListingLoading = useSelector(state => state.vpsOci?.upgrade?.listing?.loading);
  const upgradeListingData = useSelector(state => state.vpsOci?.upgrade?.listing?.data, shallowEqual);
  const currentProduct = useSelector(state => state.vpsOci?.upgrade?.listing?.data?.currentProduct, shallowEqual);
  const availableCycles = useSelector(state => state.vpsOci?.upgrade?.listing?.data?.cycles, shallowEqual);
  const availables = useSelector(state => state.vpsOci?.upgrade?.listing?.data?.availables, shallowEqual);

  const upgradeDetailsLoading = useSelector(state => state.vpsOci?.upgrade?.details?.loading);

  const { productDetails: productDetailsLocale } = useLocale();
  const listingLocale = productDetailsLocale?.vpsOci?.planDetails?.upgradeNew?.listing || {};
  const {
    header: headerLocale = {},
    periodSelector: periodSelectorLocale = {},
    planList: planListLocale = {},
    performanceBanner: performanceBannerLocale = {},
    features: featuresLocale = {},
  } = listingLocale;

  useEffect(() => {
    if (!selectedCycle && availableCycles?.length) {
      const defaultCycle = availableCycles.length === 1
        ? availableCycles[0]
        : currentProduct?.cycle;
      if (defaultCycle) setSelectedCycle(defaultCycle);
    }
  }, [currentProduct, selectedCycle, availableCycles]);

  useEffect(() => {
    if (!upgradeDetailsLoading) setLoadingPlanId(null);
  }, [upgradeDetailsLoading]);

  useEffect(() => {
    if (hostingId && upgradeListingData == null && !upgradeListingLoading) {
      dispatch(vpsOciActions.upgrade.listing.request(hostingId));
    }
  }, [dispatch, hostingId, upgradeListingData, upgradeListingLoading]);

  const handleUpgradeListingRetry = () => {
    dispatch(vpsOciActions.upgrade.listing.request(hostingId));
  };

  const currentPlanResources = useMemo(
    () => currentProduct?.resources || {},
    [currentProduct],
  );

  const processedPlans = useMemo(() => {
    if (!availables) return [];
    return availables.map(plan => ({
      ...plan,
      ...processPlanData(plan, selectedCycle, currentPlanResources),
    }));
  }, [availables, selectedCycle, currentPlanResources]);

  const currentPlanResourcesList = useMemo(
    () => buildResourcesList(currentPlanResources, null),
    [currentPlanResources],
  );

  const getButtonState = planId => ({
    loading: upgradeDetailsLoading && loadingPlanId === planId,
    disabled: upgradeDetailsLoading && loadingPlanId !== planId,
  });

  const handleSelectPlan = (plan) => {
    setLoadingPlanId(plan.productId);
    if (onSelectPlan) onSelectPlan(plan);
  };

  const lastCycle = availableCycles?.[availableCycles.length - 1] ?? null;
  const penultimateCycle = availableCycles?.[availableCycles.length - 2] ?? null;

  return (
    <Styles.UpgradeListing data-testid="vpsoci-upgrade-listing">
      <Styles.SectionHeader data-testid="vpsoci-upgrade-listing-header">
        <Styles.SectionHeaderTitle data-testid="vpsoci-upgrade-listing-header-title">
          {headerLocale.title}
        </Styles.SectionHeaderTitle>
        <Styles.SectionHeaderDescription data-testid="vpsoci-upgrade-listing-header-description">
          {headerLocale.description}
        </Styles.SectionHeaderDescription>
      </Styles.SectionHeader>

      <Styles.Content>
        {(!currentProduct || availables.length === 0) ? (
          <Styles.ErrorLoadingList data-testid="vpsoci-upgrade-listing-error">
            <Styles.ErrorLoadingListText>
              {planListLocale.errorMessages?.noFetch}
            </Styles.ErrorLoadingListText>
            <Styles.ErrorLoadingListButton>
              <Button
                onClick={handleUpgradeListingRetry}
                testId="vpsoci-upgrade-listing-retry-button"
                variant="primary"
                width={isMobile ? 'full' : 'fit'}
                size="medium2"
                version="v2"
                label={planListLocale.errorMessages?.tryAgainButton}
              />
            </Styles.ErrorLoadingListButton>
          </Styles.ErrorLoadingList>
        ) : (
          <>
            {availableCycles && (
              <PeriodSelector
                availableCycles={availableCycles}
                selectedCycle={selectedCycle}
                currentProductCycle={currentProduct?.cycle}
                lastCycle={lastCycle}
                penultimateCycle={penultimateCycle}
                locale={periodSelectorLocale}
                onCycleChange={setSelectedCycle}
              />
            )}

            <PlansList
              currentProduct={currentProduct}
              currentPlanResourcesList={currentPlanResourcesList}
              processedPlans={processedPlans}
              locale={planListLocale}
              isMobile={isMobile}
              getButtonState={getButtonState}
              onSelectPlan={handleSelectPlan}
            />
          </>
        )}
      </Styles.Content>

      <PlanFeatures locale={featuresLocale} />

      <PerformanceBanner locale={performanceBannerLocale} />
    </Styles.UpgradeListing>
  );
};

export default UpgradeListing;
